// internal deps
import React, { memo, useCallback, useEffect, useState } from "react";

// helpers
import { captureError, getCloudinaryUrl, LottieAnimation } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

// hooks
import { updateSuccessLottieData } from "../../../helpers/SuccessLottie";

// types
import type { IToingPriceComparisonBEData } from "../types";

//styles
import { LottieWrapper } from "../styles";

// components
import ErrorStateHalfCard from "./ErrorStateHalfCard";

// test-ids
import { TOING_PC_TEST_IDS } from "@swiggy-private/cart-test-ids";

// interfaces
interface ISuccessLottieProps {
    successData?: IToingPriceComparisonBEData["success_state_halfcard_data"];
    rejectionData?: IToingPriceComparisonBEData["rejection_state_halfcard_data"];
    totalSavings: number;
    onImpression?: VoidFunction;
}

const SuccessLottie: React.FC<ISuccessLottieProps> = ({ successData, rejectionData, totalSavings, onImpression }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [lottieData, setLottieData] = useState<any>(null);

    const handlePlayComplete = useCallback(() => {
        onImpression?.();
    }, [onImpression]);

    useEffect(() => {
        if (isEmpty(successData?.lottie_id)) return;

        let cancelled = false;

        const fetchAndModifyLottie = async () => {
            try {
                const lottieUrl = getCloudinaryUrl(successData?.lottie_id ?? "");
                const response = await fetch(lottieUrl);
                const data = await response.json();

                if (cancelled) return;

                const modifiedData = updateSuccessLottieData(data, successData, totalSavings);
                setLottieData(modifiedData);
            } catch (error) {
                captureError(error);
            }
        };

        fetchAndModifyLottie();

        return () => {
            cancelled = true;
        };
    }, [successData, totalSavings]);

    useEffect(() => {
        if (!isEmpty(rejectionData)) {
            onImpression?.();
        }
    }, [rejectionData, onImpression]);

    if (!isEmpty(rejectionData)) {
        return (
            <ErrorStateHalfCard
                icon={rejectionData?.icon}
                title={rejectionData?.title}
                subtitle={rejectionData?.subtitle}
                errorImage={rejectionData?.error_image}
            />
        );
    }

    if (!lottieData) return null;

    return (
        <LottieWrapper data-testid={TOING_PC_TEST_IDS?.LOTTIE}>
            <LottieAnimation
                animationData={lottieData}
                visible
                playLottie
                autoplay
                loop={false}
                backwardSpeed={1}
                forwardSpeed={1}
                playIfVisible={false}
                preserveAspect="xMidYMid meet"
                enableFrameEvent
                onPlayComplete={handlePlayComplete}
            />
        </LottieWrapper>
    );
};

export default memo(SuccessLottie);
